import { cn } from 'shared-ui';

import type { PoiHistoryEvent } from '@/api/poiHistoryApi';
import { PoiHistoryEventCard } from './PoiHistoryEventCard';

interface PoiHistoryTimelineProps {
    events: PoiHistoryEvent[];
    onViewDetails: (event: PoiHistoryEvent) => void;
}

/**
 * Vertical timeline of history events (already newest-first from the API). Each item has
 * a left rail — a solid origin-blue 10px dot plus a 4px origin-blue connecting line to
 * the next event — so the rail reads as one continuous centred timeline down the panel.
 *
 * Every dot sits inside a fixed 24px wrapper so the dots and the line share one centre
 * axis; on the first (most recent) event the wrapper is visible as a 24px halo at 20%
 * origin-blue, per the design's top-dot drop shadow.
 */
export function PoiHistoryTimeline({ events, onViewDetails }: PoiHistoryTimelineProps) {
    return (
        // Tighter left inset than the Details tab (12px vs 24px): the rail column already
        // reads as leading whitespace, so the timeline sits closer to the panel edge.
        <ol className="py-4 pr-6 pl-3">
            {events.map((event, index) => (
                <li key={event.id} className="flex gap-3">
                    <div className="relative flex w-6 shrink-0 flex-col items-center" aria-hidden="true">
                        {/* One unbroken line strip per item, spanning the item's full height so
                            consecutive items join seamlessly (the dot paints on top of it).
                            The strip is trimmed to the dot centre (mt-5 + half the 24px
                            wrapper = 32px = top-8/h-8) at the newest and oldest events so the
                            timeline starts and ends on a dot. */}
                        {events.length > 1 && (
                            <span
                                className={cn(
                                    'bg-origin-blue absolute left-1/2 w-1 -translate-x-1/2',
                                    index === 0 ? 'top-8' : 'top-0',
                                    index === events.length - 1 ? 'h-8' : 'bottom-0',
                                )}
                            />
                        )}
                        <span
                            className={cn(
                                'relative mt-5 flex size-6 shrink-0 items-center justify-center rounded-full',
                                index === 0 && 'bg-origin-blue/20',
                            )}
                        >
                            <span className="bg-origin-blue size-2.5 rounded-full" />
                        </span>
                    </div>
                    <div className="min-w-0 flex-1 pb-4">
                        <PoiHistoryEventCard event={event} onViewDetails={onViewDetails} />
                    </div>
                </li>
            ))}
        </ol>
    );
}
